import { useEffect, useState } from "react";
import { Box, Stack, Typography, TextField, Button } from "@mui/material";

const MUTED = "#64748B";
const HAIRLINE = "#E2E8F0";
const TILE = "#F8FAFC";

// How a server is reached for deploys: SSH/SFTP, or FTP with aaPanel doing the
// unzipping. Shown in two places — the Add Domain wizard and the Deploy dialog
// — so it lives here instead of being written twice.
//
// The details belong to the SERVER, not the domain: one server usually hosts
// many domains and they all deploy through the same account. Saving is the
// caller's job, because each one saves at a different moment.
//
// Already-configured servers collapse to a single line; there is nothing to
// fill in, and a repeat deploy should be one click. Parents reset the open/
// closed state by giving the element a `key`, or keep the fields on screen
// for good with `alwaysOpen`.
// `lockMethod` pins the form to one way of connecting and hides the chooser —
// the Add Domain wizard only offers FTP.
function ServerConnectionForm({
  value,
  onChange,
  serverName,
  configured,
  busy,
  note,
  lockMethod,
  alwaysOpen,
}) {
  const [editing, setEditing] = useState(false);

  // Switching to a server that has nothing saved has to show the fields.
  useEffect(() => {
    if (!configured) setEditing(true);
  }, [configured]);

  if (!value) return null;

  const label = serverName || "this server";

  if (configured && !editing && !alwaysOpen) {
    return (
      <Box
        sx={{
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 2,
          px: 2,
          py: 1.5,
          background: TILE,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{label}</Typography>
          <Typography sx={{ fontSize: 11.5, color: MUTED, wordBreak: "break-all" }}>
            {value.method === "ftp" ? "FTP" : "SSH"} · {value.username}@{value.host}:{value.port}
            {value.method === "ftp" && value.ftpRoot ? ` · root ${value.ftpRoot}` : ""}
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={() => setEditing(true)}
          disabled={busy}
          sx={{ textTransform: "none", fontWeight: 600, flexShrink: 0 }}
        >
          Change
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: `1px solid ${HAIRLINE}`,
        borderRadius: 2,
        p: 2,
        background: configured ? TILE : "#FFFBEB",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
          {configured
            ? `Connection to ${label}`
            : `${label} has no connection details yet — fill them in:`}
        </Typography>
        {configured && !alwaysOpen && (
          <Button
            size="small"
            onClick={() => setEditing(false)}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 600, flexShrink: 0 }}
          >
            Done
          </Button>
        )}
      </Stack>

      {lockMethod ? (
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: MUTED, mb: 1.5 }}>
          {lockMethod === "ftp" ? "FTP + aaPanel API" : "SSH / SFTP"}
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          {["ssh", "ftp"].map((m) => (
            <Button
              key={m}
              size="small"
              variant={value.method === m ? "contained" : "outlined"}
              onClick={() =>
                onChange((c) => ({ ...c, method: m, port: m === "ftp" ? "21" : "22" }))
              }
              disabled={busy}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {m === "ssh" ? "SSH / SFTP" : "FTP + aaPanel API"}
            </Button>
          ))}
        </Stack>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 1.5, mb: 1.5 }}>
        <TextField
          size="small"
          label="Host"
          value={value.host}
          onChange={(e) => onChange((c) => ({ ...c, host: e.target.value }))}
          disabled={busy}
        />
        <TextField
          size="small"
          label="Port"
          value={value.port}
          onChange={(e) => onChange((c) => ({ ...c, port: e.target.value }))}
          disabled={busy}
        />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 1.5 }}>
        <TextField
          size="small"
          label="Username"
          value={value.username}
          onChange={(e) => onChange((c) => ({ ...c, username: e.target.value }))}
          disabled={busy}
        />
        <TextField
          size="small"
          type="password"
          label="Password"
          // A saved password never comes back from the server, so an empty box
          // means "keep the one already stored".
          placeholder={configured ? "saved — type to change" : ""}
          autoComplete="new-password"
          value={value.password}
          onChange={(e) => onChange((c) => ({ ...c, password: e.target.value }))}
          disabled={busy}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Box>

      {value.method === "ftp" && (
        <TextField
          size="small"
          fullWidth
          label="FTP root (where the FTP user lands)"
          value={value.ftpRoot}
          onChange={(e) => onChange((c) => ({ ...c, ftpRoot: e.target.value }))}
          disabled={busy}
        />
      )}

      {note && (
        <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 1.5 }}>{note}</Typography>
      )}
    </Box>
  );
}

// Builds the form's state from a server record (either the /servers list shape
// or the deploy-info shape). Passwords are never included — they stay on the
// server, encrypted.
export function connFromServer(data, forceMethod) {
  if (!data) return null;
  const ftp = forceMethod ? forceMethod === "ftp" : data.deployMethod === "ftp";
  return {
    method: ftp ? "ftp" : "ssh",
    host: (ftp ? data.ftpHost : data.sshHost ?? data.host) || "",
    port: String((ftp ? data.ftpPort : data.sshPort ?? data.port) || (ftp ? 21 : 22)),
    username: (ftp ? data.ftpUsername : data.sshUsername ?? data.username) || "",
    password: "",
    // Only fall back for a server that was never set up — overwriting a saved
    // root would break every later deploy on it.
    ftpRoot: data.ftpRoot || "/www/wwwroot",
  };
}

// The payload that saves these details onto a server. Merge it into the full
// server record before PUTting: the API replaces the whole row, so a partial
// save would wipe the other fields.
export function connToServerPayload(conn) {
  return {
    deployMethod: conn.method,
    ...(conn.method === "ftp"
      ? {
          ftpHost: conn.host,
          ftpPort: conn.port ? Number(conn.port) : 21,
          ftpUsername: conn.username,
          ftpRoot: conn.ftpRoot,
          ...(conn.password ? { ftpPassword: conn.password } : {}),
        }
      : {
          sshHost: conn.host,
          sshPort: conn.port ? Number(conn.port) : 22,
          sshUsername: conn.username,
          ...(conn.password ? { sshPassword: conn.password } : {}),
        }),
  };
}

export default ServerConnectionForm;
